/*
  Brings the interactive parts of BAKED REPLO sections back to life.

  Why this exists. The Replo -> Liquid conversion inlines Replo's rendered markup but drops
  its runtime, so anything Replo drove with JavaScript is left as dead HTML. On AIR PRO's
  "Find your Pulsio" band (sections/airpros3.liquid) the section's three <script> tags are
  EMPTY, and with them went:

    - the Pulsio Guns / Competitors tab switcher, so clicking Competitors did nothing and
      the panel stayed on whichever tab Replo happened to render active
    - six carousels, so every one of them was stuck on its first slide

  Rather than rebuild the band, this drives the markup that is already there. Replo emits
  Radix UI's ARIA, which is well-formed enough to work against:

    - each [role=tab] carries aria-controls pointing at its [role=tabpanel]
    - the active panel is inline `display: contents`, the inactive one `display: none`
    - carousels expose [data-replo-part="slides"] (the viewport), "slide-track" (the flex
      row Replo used to translate) and "nav-button" with data-direction

  Two things to know about that markup. Radix ids contain colons
  ("radix-:R7rr2:-content-..."), so they must be resolved with getElementById rather than
  querySelector, where the colon would parse as a pseudo-class. And the six carousels on
  this band NEST without repeating data-replo-carousel on the inner ones, so they are
  grouped by data-replo-root-id instead — grouping by nearest carousel ancestor collapsed
  all six into one and paired a viewport with another carousel's track.

  Carousels are converted to native scroll-snap instead of reimplementing Replo's
  transform maths. The slides already have fixed flex widths, so making the viewport a
  scroll container is enough, and it brings touch swipe and keyboard scrolling for free.

  Opt-in per section: a section pushes its id onto window.__reploInteractiveRoots, and only
  those subtrees are touched. Nothing here runs against pages that did not ask for it.

  Degrades honestly: with no JavaScript the band renders exactly as it does today — first
  tab, first slide. Nothing that works now can break by this file failing to load.
*/

(function () {
  if (window.__reploInteractiveInit) return;
  window.__reploInteractiveInit = true;

  /* Replo groups the parts of one carousel with data-replo-root-id
     ("replo-carousel:Rfmrr2:"). That is the only reliable key here: the nested carousels do
     NOT repeat data-replo-carousel on themselves, so grouping by nearest carousel ancestor
     collapses all six on this section into one and pairs a viewport with another carousel's
     track. Measured before this was fixed: 4 tracks all resolved to the same "owner". */
  function sameRoot(root, part, rid) {
    return Array.prototype.filter.call(
      root.querySelectorAll('[data-replo-part="' + part + '"]'),
      function (el) { return el.getAttribute('data-replo-root-id') === rid; }
    );
  }

  /* Nav buttons carry no root-id, so they are matched structurally: walk up from the button
     to the nearest ancestor that contains a slides viewport. Prev button, viewport and next
     button are siblings, so the innermost such ancestor is the button's own carousel. */
  function viewportFor(btn) {
    var n = btn.parentElement;
    while (n) {
      var vp = n.querySelector('[data-replo-part="slides"]');
      if (vp) return vp;
      n = n.parentElement;
    }
    return null;
  }

  function initTabs(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[role="tablist"]'), function (list) {
      var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      if (tabs.length < 2) return;

      var panels = tabs.map(function (tab) {
        var id = tab.getAttribute('aria-controls');
        return id ? document.getElementById(id) : null;   // colons in the id — not querySelector
      });
      if (panels.some(function (p) { return !p; })) return;   // pairing is not what we expect; leave it alone

      function select(index) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.setAttribute('data-state', on ? 'active' : 'inactive');
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.setAttribute('tabindex', on ? '0' : '-1');
          var panel = panels[i];
          panel.setAttribute('data-state', on ? 'active' : 'inactive');
          /* `contents` rather than `block`: Replo's own active panel used it, and the
             panel is a pass-through wrapper whose children carry the grid/flex layout. */
          panel.style.display = on ? 'contents' : 'none';
          panel.hidden = !on;
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function (e) { e.preventDefault(); select(i); });
        tab.addEventListener('keydown', function (e) {
          var next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
          if (next === null) return;
          e.preventDefault();
          next = (next + tabs.length) % tabs.length;
          tabs[next].focus();
          select(next);
        });
      });

      /* Adopt whichever tab Replo rendered active so nothing moves on load. */
      var current = tabs.findIndex(function (t) { return t.getAttribute('data-state') === 'active'; });
      select(current < 0 ? 0 : current);
    });
  }

  function initCarousels(root) {
    /* Clear every track transform first. Replo left some mid-animation (one measured at
       translate3d(-956px)), which offsets the slides against a native scroll container. */
    Array.prototype.forEach.call(root.querySelectorAll('[data-replo-part="slide-track"]'), function (t) {
      t.style.transform = 'none';
      t.style.transition = 'none';
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-replo-part="slides"]'), function (viewport) {
      var rid = viewport.getAttribute('data-replo-root-id');
      if (!rid) return;
      var track = sameRoot(viewport, 'slide-track', rid)[0];
      if (!track) return;
      var slides = sameRoot(track, 'slide', rid).filter(function (s) { return s.parentElement === track; });

      /* Drop slides that carry nothing. The Competitors comparison ships as three slides
         where the middle one is a bare `<div class="airpros3-r-7b3thm"></div>` — no text, no
         media, one element — but it still claims a full 170px slot. That pushed the
         "Avg Competitor" column into third position and off the screen edge, which is why
         the tab looked like it was missing its second column.

         The test is deliberately strict: no text, no img/video/svg/picture, and nothing in
         the subtree painting a background image. A decorative slide that shows anything at
         all is left alone. */
      slides = slides.filter(function (s) {
        var blank = s.textContent.replace(/\s+/g, '').length === 0 &&
          !s.querySelector('img,video,svg,picture') &&
          !Array.prototype.some.call(s.querySelectorAll('*'), function (e) {
            return getComputedStyle(e).backgroundImage !== 'none';
          }) &&
          getComputedStyle(s).backgroundImage === 'none';
        if (blank) s.style.display = 'none';
        return !blank;
      });

      if (slides.length < 2) return;

      viewport.style.overflowX = 'auto';
      viewport.style.overflowY = 'hidden';
      viewport.style.scrollbarWidth = 'none';
      viewport.style.webkitOverflowScrolling = 'touch';

      /* `proximity`, not `mandatory`. With mandatory, a programmatic scroll is pulled back
         to the snap point it started from and the buttons do nothing: measured asking for
         offset 225 and landing on 135 every time, in both directions. Proximity still snaps
         a finger swipe tidily but leaves a scripted scroll alone. */
      viewport.style.scrollSnapType = 'x proximity';

      /* `auto`, not `smooth`. A smooth programmatic scroll gets cancelled here and returns
         to 0 — a jump that lands is worth more than a glide that does not. Touch and
         trackpad scrolling are unaffected either way; scroll-behavior only governs
         scripted and anchor scrolls. */
      viewport.style.scrollBehavior = 'auto';

      slides.forEach(function (s) { s.style.scrollSnapAlign = 'start'; });

      /* Slides are variable width on this band (offsets 0, 135, 225, 333, 436, 555), so a
         fixed step lands mid-slide. Step to the next slide's own offset instead. */
      viewport.__reploGo = function (dir) {
        var cur = viewport.scrollLeft;
        var offs = slides.map(function (s) { return s.offsetLeft; });
        var target = dir > 0
          ? offs.find(function (o) { return o > cur + 2; })
          : offs.slice().reverse().find(function (o) { return o < cur - 2; });
        if (target === undefined) return;
        viewport.scrollLeft = target;
      };
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-replo-part="nav-button"]'), function (btn) {
      if (btn.__reploBound) return;
      var viewport = viewportFor(btn);
      if (!viewport || !viewport.__reploGo) return;
      btn.__reploBound = true;
      var back = btn.getAttribute('data-direction') === 'previous';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        viewport.__reploGo(back ? -1 : 1);
      });
    });
  }

  function boot() {
    var ids = window.__reploInteractiveRoots || [];
    ids.forEach(function (id) {
      var root = document.getElementById('shopify-section-' + id);
      if (!root || root.__reploInteractiveDone) return;
      root.__reploInteractiveDone = true;
      try { initTabs(root); } catch (e) { /* a dead tab is better than a dead page */ }
      try { initCarousels(root); } catch (e) { /* same */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* The theme editor re-renders a section without a page load. */
  document.addEventListener('shopify:section:load', boot);
})();
